import { Link } from 'react-router-dom';
import SEO from '@/components/common/SEO';
import { SOURCING_TIERS, METHODOLOGY_REFUSALS } from '@/content/financial/editorial';

export default function Methodology() {
  return (
    <div className="bg-white dark:bg-surface-dark">
      <SEO
        title="How We Source Numbers"
        description="The sourcing tiers behind every case study in the Financial section, what each badge actually means, and what that sourcing cannot tell you."
        path="/financial/methodology"
      />

      <div className="mx-auto max-w-2xl px-4 py-14 sm:px-6 sm:py-20">
        <nav aria-label="Breadcrumb" className="mb-4 text-sm text-ink-500 dark:text-ink-400">
          <Link to="/financial" className="hover:underline">
            Financial
          </Link>
          <span className="mx-2" aria-hidden="true">
            /
          </span>
          <span className="text-ink-700 dark:text-ink-200">Methodology</span>
        </nav>

        <p className="text-[11px] font-bold uppercase tracking-wide text-cheong-500 dark:text-cheong-dark">
          Methodology
        </p>
        <h1 className="mt-3 text-3xl font-bold leading-tight text-ink-900 dark:text-ink-50 sm:text-4xl">
          How we source numbers
        </h1>
        <p lang="ko" className="mt-1 font-korean text-lg text-ink-500 dark:text-ink-400">
          수치 출처 기준
        </p>
        <p className="mt-5 text-lg leading-8 text-ink-700 dark:text-ink-200">
          The most common failure in this genre is presenting a modelled number as a reported one. A
          figure invented for a thumbnail and a figure pulled from a bank statement look identical
          on a slide, so we label every case study with where its economics came from, and what that
          sourcing cannot tell you.
        </p>

        <div className="mt-12 space-y-4">
          {SOURCING_TIERS.map((tier) => (
            <section
              key={tier.badge}
              className="rounded-xl border border-ink-200 bg-white p-5 shadow-sm dark:border-surface-dark-border dark:bg-surface-dark-card sm:p-6"
            >
              <h2 className="text-lg font-semibold text-ink-900 dark:text-ink-50">{tier.badge}</h2>
              <p className="mt-2.5 leading-7 text-ink-700 dark:text-ink-200">{tier.meaning}</p>
              <p className="mt-3 text-sm leading-6 text-ink-500 dark:text-ink-400">
                <strong className="font-semibold text-ink-700 dark:text-ink-200">Limits.</strong>{' '}
                {tier.limits}
              </p>
            </section>
          ))}
        </div>

        <section className="mt-14">
          <h2 className="mb-4 text-2xl font-bold text-ink-900 dark:text-ink-50">
            What we will not do
          </h2>
          <ul className="space-y-3">
            {METHODOLOGY_REFUSALS.map((item) => (
              <li key={item} className="flex gap-3 leading-7 text-ink-700 dark:text-ink-200">
                <span
                  className="mt-3 h-1.5 w-1.5 shrink-0 rounded-full bg-jeok-500"
                  aria-hidden="true"
                />
                {item}
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-14">
          <h2 className="mb-4 text-2xl font-bold text-ink-900 dark:text-ink-50">Corrections</h2>
          <p className="leading-7 text-ink-700 dark:text-ink-200">
            If a figure here is wrong, we want to know and we will correct it in place with a dated
            note. Accuracy is the entire product — a finance section that quietly edits its numbers
            has nothing left to offer.{' '}
            <Link to="/contact" className="font-medium text-cheong-500 underline dark:text-cheong-dark">
              Tell us what we got wrong
            </Link>
            .
          </p>
        </section>

        <p className="mt-14 border-t border-ink-200 pt-6 text-sm leading-relaxed text-ink-500 dark:border-surface-dark-border dark:text-ink-400">
          See also the{' '}
          <Link
            to="/financial/disclosures"
            className="font-medium text-cheong-500 underline dark:text-cheong-dark"
          >
            full disclosures
          </Link>{' '}
          for what this section is, what it is not, and what you should not rely on it for.
        </p>
      </div>
    </div>
  );
}
